import { redactDemoText } from './demoRedaction';

const GUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const IP_PORT_RE = /\b(?:\d{1,3}\.){3}\d{1,3}(?::\d{1,5})?\b/g;
const CVE_RE = /\bCVE-\d{4}-\d{4,7}\b/gi;

export function isDemoPolishEnabled(env = import.meta.env) {
  const value = String(env?.VITE_ENABLE_DEMO_POLISH || '').trim().toLowerCase();
  if (value === 'true' || value === '1' || value === 'yes' || value === 'on') return true;
  return false;
}

export function isDemoArtifact(value) {
  const text = String(value || '').toLowerCase();
  return text.includes('alpha-e2e-test-2')
    || text.includes('test.com')
    || text.includes('platformtest@')
    || text.includes('client@test')
    || text.includes('msp@test')
    || text.includes('sss-dast-runner-10.100.0.2');
}

export function isGuidLike(value) {
  return /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i.test(String(value || ''));
}

export function normalizeAlertMessage(message) {
  let value = String(message || '').trim().toLowerCase();
  value = value.replace(GUID_RE, '{guid}').replace(IP_PORT_RE, '{asset}').replace(/\s+/g, ' ');

  if (value.includes('cloudtrail') && value.includes('not enabled')) return 'cloudtrail-not-enabled';
  if (value.includes('root account') && value.includes('last 90 days')) return 'root-account-used-last-90-days';
  if (value.includes('conditional access') && value.includes('mfa')) return 'conditional-access-mfa-not-enforced';
  if (value.includes('credential exposure')) return 'credential-exposure';

  const cve = String(message || '').match(CVE_RE)?.[0];
  if (cve) return cve.toUpperCase();
  return value || 'unknown-alert';
}

export function extractAlertAsset(message) {
  const text = String(message || '');
  return text.match(IP_PORT_RE)?.[0]?.toLowerCase()
    || text.match(CVE_RE)?.[0]?.toUpperCase()
    || 'general';
}

export function alertGroupKey(alert) {
  const client = String(alert?.client || alert?.clientName || 'unknown-client').trim().toLowerCase();
  return `${client}|${normalizeAlertMessage(alert?.msg || alert?.message)}|${extractAlertAsset(alert?.msg || alert?.message)}`;
}

export function groupAlerts(alerts, { severity, limit } = {}) {
  const groups = new Map();
  for (const alert of alerts || []) {
    if (!alert) continue;
    const normalizedSeverity = String(alert.severity || '').toLowerCase();
    if (severity && normalizedSeverity !== severity) continue;
    const key = alertGroupKey(alert);
    const existing = groups.get(key);
    const time = alert.time || alert.createdAt || null;
    if (!existing) {
      groups.set(key, { ...alert, severity: normalizedSeverity, count: 1, latestTime: time, rawAlerts: [alert] });
    } else {
      existing.count += 1;
      existing.rawAlerts.push(alert);
      if (time && (!existing.latestTime || new Date(time) > new Date(existing.latestTime))) {
        existing.latestTime = time;
        existing.time = time;
      }
    }
  }

  const grouped = [...groups.values()].sort((a, b) => {
    const aTime = a.latestTime ? new Date(a.latestTime).getTime() : 0;
    const bTime = b.latestTime ? new Date(b.latestTime).getTime() : 0;
    return bTime - aTime;
  });
  return typeof limit === 'number' ? grouped.slice(0, limit) : grouped;
}

export function countAlertsByFilter(alerts) {
  const grouped = groupAlerts(alerts);
  return {
    all: grouped.length,
    unread: grouped.filter(a => !a.read).length,
    critical: grouped.filter(a => a.severity === 'critical').length,
    high: grouped.filter(a => a.severity === 'high').length,
    medium: grouped.filter(a => a.severity === 'medium').length,
  };
}

export function visibleDemoClients(clients) {
  if (!isDemoPolishEnabled()) return clients || [];
  const demoEnvironment = (clients || []).filter(client => {
    const name = client?.name || client?.companyName || '';
    return String(name).trim().toLowerCase() === 'demo environment';
  });
  if (demoEnvironment.length > 0) return demoEnvironment;

  return (clients || []).filter(client => {
    const name = client?.name || client?.companyName || '';
    return !isDemoArtifact(name);
  });
}

export function visibleDemoMembers(members) {
  if (!isDemoPolishEnabled()) return members || [];
  return (members || [])
    .filter(member => !isDemoArtifact(`${member?.name || member?.displayName || ''} ${member?.email || ''}`))
    .map(normalizeTeamMemberName)
    .map((member, index) => ({
      ...member,
      name: demoTeamName(member, index),
      displayName: member.displayName ? demoTeamName(member, index) : member.displayName,
      email: demoTeamEmail(member, index),
    }));
}

function normalizeTeamMemberName(member) {
  const name = member?.name || member?.displayName || '';
  if (name.trim().toUpperCase() !== 'SETH PRIESTNER') return member;

  return {
    ...member,
    name: member?.name ? 'Seth Priestner' : member?.name,
    ...(member?.displayName ? { displayName: 'Seth Priestner' } : {}),
  };
}

function demoTeamEmail(member, index) {
  const role = String(member?.role ?? '').toLowerCase();
  if (role.includes('viewer')) return 'viewer@demo-msp.example';
  if (role.includes('analyst')) return 'analyst@demo-msp.example';
  if (index === 0) return 'admin@demo-msp.example';
  return `team${index + 1}@demo-msp.example`;
}

function demoTeamName(member, index) {
  const role = String(member?.role ?? '').toLowerCase();
  if (role.includes('viewer')) return 'Client Success Viewer';
  const names = [
    'Partner Admin',
    'Security Analyst',
    'Compliance Lead',
    'Service Desk Lead',
    'Executive Reviewer',
    'Report Coordinator',
  ];
  return names[index] || `Demo Team Member ${index + 1}`;
}

export function demoVisibleAlerts(alerts) {
  const rows = alerts || [];
  if (!isDemoPolishEnabled()) return rows;

  return rows
    .map(alert => ({
      ...alert,
      client: redactDemoText(alert?.client || alert?.clientName || ''),
      clientName: redactDemoText(alert?.clientName || alert?.client || ''),
      msg: redactDemoText(alert?.msg || alert?.message || ''),
      message: redactDemoText(alert?.message || alert?.msg || ''),
    }))
    .filter(alert => {
      const client = String(alert.client || alert.clientName || '').toLowerCase();
      const msg = String(alert.msg || alert.message || '').toLowerCase();
      return client.includes('demo environment') || msg.includes('demo environment') || msg.includes('demo-client');
    });
}

export function demoVisibleReports(reports) {
  const rows = reports || [];
  if (!isDemoPolishEnabled()) return rows;

  return rows
    .map(report => ({
      ...report,
      clientName: redactDemoText(report?.clientName || ''),
      name: redactDemoText(report?.name || report?.title || ''),
      title: redactDemoText(report?.title || report?.name || ''),
      errorMessage: redactDemoText(report?.errorMessage || report?.lastErrorMessage || report?.failureReason || report?.statusMessage || ''),
    }))
    .filter(report => String(report.clientName || '').trim().toLowerCase() === 'demo environment');
}

export function reportCadenceKey(report) {
  if (isDemoPolishEnabled() && report?.demoMetadata?.cadenceKey) return report.demoMetadata.cadenceKey;
  const type = report?.type || 'monthly';
  const date = report?.gen ? new Date(report.gen) : new Date(0);
  if (type === 'qbr') return `${type}:Q${Math.floor(date.getMonth() / 3) + 1}:${date.getFullYear()}`;
  return `${type}:${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function cleanReportCadence(reports) {
  if (!isDemoPolishEnabled()) return reports || [];
  const seen = new Set();
  return (reports || []).filter(report => {
    const key = `${report.clientId || 'unknown'}:${reportCadenceKey(report)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function reportSizeWarning(report) {
  if (isDemoPolishEnabled()) return '';
  const bytes = Number(report?.sizeBytes || 0);
  return bytes >= 25 * 1024 * 1024 ? 'Large export; verify raw scan data is not embedded.' : '';
}

export function seededCloudResult(provider) {
  if (!isDemoPolishEnabled()) return null;
  return {
    provider,
    valid: true,
    scanReady: true,
    testedAt: new Date().toISOString(),
    message: 'Verified for demo tenant',
  };
}
